import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt } from '../../../data/metrics';
import { sourceMeta } from '../../../data/reservationSources';
import type {
  ConnectedSourceId,
  DrillPanel,
  SourceBreakdown,
} from '../../../types';
import {
  mixBarShareLabel,
  mixBarVolume,
} from '../../SourceMixPanel';
import { DrillTile } from './DrillTile';
import { DrillSourceRateList, isRateMixMetric } from './DrillSourceRateList';
import { SourceMixInspector } from './SourceMixInspector';
import { DrillChartLegend } from './charts/DrillChartLegend';
import { DrillDonutChart } from './charts/DrillDonutChart';

export function DrillSourceSection({
  metricKey,
  secondaryKey,
  breakdown,
  bySourcePanel,
  connected,
  bucketLabels,
  aggregationLabel,
  periodLabel,
  comparable,
  compareLabel,
  moneyUnit,
  metricValue,
  metricSeries,
  fmtVal,
  fmtVal2,
}: {
  metricKey: string;
  secondaryKey?: string;
  breakdown?: SourceBreakdown;
  bySourcePanel?: Extract<DrillPanel, { kind: 'bySource' }>;
  connected?: ConnectedSourceId[];
  bucketLabels?: string[];
  aggregationLabel?: string;
  periodLabel?: string;
  comparable?: boolean;
  compareLabel?: string;
  moneyUnit?: string | null;
  metricValue?: number;
  metricSeries?: number[];
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
}) {
  const hasMix = !!breakdown?.sources?.length && !!breakdown.totalCovers;
  const shareNoun = mixBarShareLabel(metricKey);

  const mix = useMemo(() => {
    if (!hasMix || !breakdown) return null;
    const vols = breakdown.sources.map(s => ({
      s,
      vol: mixBarVolume(s, metricKey),
    }));
    const mixTotal = vols.reduce((a, x) => a + x.vol, 0);
    const useCovers = mixTotal <= 0;
    const total = useCovers ? breakdown.totalCovers || 1 : mixTotal;
    const ranked = [...vols].sort((a, b) => {
      const va = useCovers ? a.s.covers : a.vol;
      const vb = useCovers ? b.s.covers : b.vol;
      return vb - va;
    });
    const slices = ranked.map(({ s, vol }) => {
      const value = useCovers ? s.covers : vol;
      const m = sourceMeta(s.source);
      return {
        name: m.label,
        value,
        color: m.color,
        share: +((value / total) * 100).toFixed(1),
        compareShare: s.compareShare,
        source: s.source,
      };
    });
    // The donut skips empty slices; the legend lists exactly what it draws.
    const kept = slices.filter((s, i) => (s.share >= 5 || i < 4) && s.value > 0);
    return { kept, shareNoun };
  }, [breakdown, hasMix, metricKey, shareNoun]);

  const legacy = useMemo(() => {
    if (hasMix || !bySourcePanel) return null;
    const sorted = [...bySourcePanel.rows].sort((a, b) => b.value - a.value);
    const top = sorted.filter((r, i) => r.share >= 5 || i < 4);
    const slices = top
      .map(r => {
        const m = sourceMeta(r.source);
        return { name: m.label, value: Math.abs(r.value), color: m.color };
      })
      .filter(s => s.value > 0);
    return { slices };
  }, [hasMix, bySourcePanel]);

  const inspector = useMemo(() => {
    if (hasMix) {
      return (
        <SourceMixInspector
          breakdown={breakdown}
          connected={connected ?? []}
          bucketLabels={bucketLabels ?? []}
          aggregationLabel={aggregationLabel}
          periodLabel={periodLabel}
          comparable={comparable ?? true}
          compareLabel={compareLabel}
          metricKey={metricKey}
          secondaryKey={secondaryKey}
          moneyUnit={moneyUnit}
          metricValue={metricValue}
          metricSeries={metricSeries}
        />
      );
    }
    if (!bySourcePanel) return null;
    const sorted = [...bySourcePanel.rows].sort((a, b) => b.value - a.value);
    return (
      <div className="dcrux-si-list">
        {sorted.map(r => {
          const m = sourceMeta(r.source);
          return (
            <div className="dcrux-si-row" key={r.source}>
              <span className="dcrux-si-name">
                <span
                  className="dcrux-comp-dot"
                  style={{ background: m.color, marginRight: 6 }}
                />
                {m.label}
              </span>
              <span className="dcrux-si-val">
                {fmtVal(r.value)}
                {r.value2 != null && fmtVal2 ? ` · ${fmtVal2(r.value2)}` : ''}
              </span>
            </div>
          );
        })}
      </div>
    );
  }, [
    hasMix,
    breakdown,
    connected,
    bucketLabels,
    aggregationLabel,
    periodLabel,
    comparable,
    compareLabel,
    metricKey,
    secondaryKey,
    moneyUnit,
    metricValue,
    bySourcePanel,
    fmtVal,
    fmtVal2,
  ]);

  if (!hasMix && !bySourcePanel) return null;

  // No-shows and cancellations read as a rate per channel first, their share
  // of the events second.
  if (hasMix && isRateMixMetric(metricKey)) {
    return (
      <DrillTile
        sectionId="source-mix"
        span={4}
        title={i18n.t('performance:drillUi.sourceMix')}
        meta={i18n.t(
          metricKey === 'cancel'
            ? 'performance:noshowSubstance.source.metaCancel'
            : 'performance:noshowSubstance.source.metaNoshow',
        )}
        inspector={inspector}
      >
        <DrillSourceRateList breakdown={breakdown} metricKey={metricKey} limit={6} />
      </DrillTile>
    );
  }

  const slices: {
    name: string;
    value: number;
    color?: string;
    share?: number;
    compareShare?: number;
  }[] = mix?.kept ?? legacy?.slices ?? [];

  return (
    <DrillTile
      sectionId="source-mix"
      span={4}
      title={i18n.t('performance:drillUi.sourceMix')}
      meta={i18n.t('performance:drillUi.shareOf', { noun: shareNoun })}
      inspector={inspector}
      n={
        hasMix && breakdown
          ? i18n.t('performance:drillUi.nBasis', {
              reservations: fmt.num(
                breakdown.sources.reduce((a, s) => a + (s.reservations || 0), 0),
              ),
              guests: fmt.num(breakdown.totalCovers),
            })
          : undefined
      }
    >
      <div className="ddrill-donut-row">
        <DrillDonutChart slices={slices} fmtVal={fmtVal} height={148} />
        <DrillChartLegend
          items={slices.map(s => {
            const share = s.share != null ? `${s.share}%` : undefined;
            const cmp = s.compareShare;
            const pt =
              comparable && cmp != null && s.share != null
                ? +(s.share - cmp).toFixed(1)
                : null;
            const d =
              pt != null ? `${pt >= 0 ? '+' : ''}${pt}pt` : undefined;
            return {
              name: s.name,
              color: s.color,
              detail: share,
              delta: d,
              deltaTitle:
                d && cmp != null
                  ? `${compareLabel ?? i18n.t('performance:drillUi.compare')}: ${cmp}%`
                  : undefined,
            };
          })}
        />
      </div>
    </DrillTile>
  );
}
